import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Clock,
  Globe,
  Save,
  Loader2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  Calendar,
  AlertTriangle,
  Trash2,
  Map,
  ImageIcon,
  Search,
  Wrench,
  Download
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import AdminMediaLibrary from './AdminMediaLibrary';
import { getMediaUrl, toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MaintenanceTab = () => {
  const [migrating, setMigrating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const { token } = useAuth();
  const { toast } = useToast();

  const runMigration = async () => {
    setMigrating(true);
    setResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/migrate-media-urls`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      toast({
        title: 'Migrering fullfort',
        description: `${res.data.documents_updated} dokumenter oppdatert`,
      });
    } catch (err) {
      toast({
        title: 'Feil',
        description: 'Kunne ikke kjore migrering',
        variant: 'destructive'
      });
    } finally {
      setMigrating(false);
    }
  };

  const exportWordPress = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/export-wordpress`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sandtec-export-${new Date().toISOString().slice(0,10)}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'Eksport fullfort', description: 'WordPress XML-fil lastet ned' });
    } catch (err) {
      toast({ title: 'Feil', description: 'Kunne ikke eksportere data', variant: 'destructive' });
    }
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/sync-media-to-storage`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });
      setSyncResult(res.data);
      toast({
        title: 'Synkronisering fullfort',
        description: `${res.data.uploaded} filer lastet opp, ${res.data.failed} feilet`,
      });
    } catch (err) {
      toast({
        title: 'Feil',
        description: 'Kunne ikke synkronisere til lagring',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Wrench className="w-5 h-5 text-red-400" />
          Vedlikeholdsverktoy
        </CardTitle>
        <CardDescription>Verktoy for vedlikehold og feilretting av data</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-blue-400" />
            Fiks bilde-URLer
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Normaliserer alle bilde-URLer i databasen til relative stier. 
            Dette fikser bilder som peker til feil domene etter migrering eller deploy.
          </p>
          <div className="flex items-center gap-4">
            <Button
              onClick={runMigration}
              disabled={migrating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="migrate-media-urls-btn"
            >
              {migrating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Kjorer...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Kjor migrering</>
              )}
            </Button>
            {result && (
              <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                {result.documents_updated} dokumenter oppdatert
              </Badge>
            )}
          </div>
        </div>

        <div className="p-4 bg-gray-800 rounded-lg border border-green-700/50">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-green-400" />
            Synkroniser til persistent lagring
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Laster opp alle lokale mediefiler til persistent Object Storage. 
            Sikrer at bilder overlever pod-restart og deploy.
            Kjor dette etter at du har lastet opp nye bilder.
          </p>
          <div className="flex items-center gap-4">
            <Button
              onClick={runSync}
              disabled={syncing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="sync-media-storage-btn"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Synkroniserer...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Synkroniser mediefiler</>
              )}
            </Button>
            {syncResult && (
              <Badge className="bg-green-600/20 text-green-400 border-green-600/50">
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                {syncResult.uploaded} lastet opp, {syncResult.failed} feilet
              </Badge>
            )}
          </div>
        </div>

        <div className="p-4 bg-gray-800 rounded-lg border border-yellow-700/50">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
            <Download className="w-4 h-4 text-yellow-400" />
            Eksporter innhold (WordPress XML)
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Last ned alle sider, tjenester, anmeldelser og media som WordPress-kompatibel XML-fil (WXR).
            Kan importeres direkte i WordPress via Verktoy - Importer.
          </p>
          <Button
            onClick={exportWordPress}
            className="bg-yellow-600 hover:bg-yellow-700"
            data-testid="export-wordpress-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Last ned WordPress XML
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminCompanyInfo = () => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState(null);
  const [isLogoMediaOpen, setIsLogoMediaOpen] = useState(false);
  
  const { toast } = useToast();
  const { token } = useAuth();
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchCompanyInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-info`, { headers });
      setCompanyInfo(response.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching company info:', error);
      toast({ title: 'Feil', description: 'Kunne ikke hente bedriftsinformasjon', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);
  
  useEffect(() => {
    fetchCompanyInfo();
  }, [fetchCompanyInfo]);
  
  const handleFieldChange = (section, field, value) => {
    setCompanyInfo(prev => {
      if (section) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
    setHasChanges(true);
  };
  
  const handleOpeningHoursChange = (day, field, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: {
          ...prev.opening_hours[day],
          [field]: value
        }
      }
    }));
    setHasChanges(true);
  };
  
  // Geocoding funksjon - konverter adresse til koordinater
  const handleGeocode = async () => {
    if (!companyInfo?.address) {
      toast({ title: 'Feil', description: 'Ingen adresse å geocode', variant: 'destructive' });
      return;
    }
    
    setGeocoding(true);
    setGeocodeResult(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/company-info/geocode`,
        companyInfo.address,
        { headers }
      );
      
      if (response.data.success) {
        // Oppdater koordinater lokalt
        setCompanyInfo(prev => ({
          ...prev,
          coordinates: response.data.coordinates
        }));
        setGeocodeResult({ success: true, message: response.data.message });
        setHasChanges(true);
        toast({ 
          title: 'Koordinater funnet!', 
          description: `${response.data.coordinates.lat.toFixed(6)}, ${response.data.coordinates.lng.toFixed(6)}` 
        });
      } else {
        setGeocodeResult({ success: false, message: response.data.message });
        toast({ title: 'Ikke funnet', description: response.data.message, variant: 'destructive' });
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setGeocodeResult({ success: false, message: 'Feil ved geocoding' });
      toast({ title: 'Feil', description: 'Kunne ikke geocode adressen', variant: 'destructive' });
    } finally {
      setGeocoding(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/company-info`, companyInfo, { headers });
      toast({ title: 'Lagret!', description: 'Bedriftsinformasjon er oppdatert' });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving company info:', error);
      toast({ title: 'Feil', description: 'Kunne ikke lagre endringer', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const addCertification = () => {
    setCompanyInfo(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), '']
    }));
    setHasChanges(true);
  };
  
  const removeCertification = (index) => {
    setCompanyInfo(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };
  
  const updateCertification = (index, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => i === index ? value : cert)
    }));
    setHasChanges(true);
  };
  
  // Special hours handlers
  const handleSpecialHoursChange = (field, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      special_hours: {
        ...prev.special_hours,
        [field]: value
      }
    }));
    setHasChanges(true);
  };
  
  const addSpecialDay = () => {
    const newDay = {
      date: new Date().toISOString().split('T')[0],
      label: '',
      open: '',
      close: '',
      is_open: false
    };
    setCompanyInfo(prev => ({
      ...prev,
      special_hours: {
        ...prev.special_hours,
        hours: [...(prev.special_hours?.hours || []), newDay]
      }
    }));
    setHasChanges(true);
  };
  
  const updateSpecialDay = (index, field, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      special_hours: {
        ...prev.special_hours,
        hours: prev.special_hours.hours.map((day, i) => 
          i === index ? { ...day, [field]: value } : day
        )
      }
    }));
    setHasChanges(true);
  };
  
  const removeSpecialDay = (index) => {
    setCompanyInfo(prev => ({
      ...prev,
      special_hours: {
        ...prev.special_hours,
        hours: prev.special_hours.hours.filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };
  
  // Bygg Google Maps preview URL
  const buildMapPreviewUrl = (info) => {
    if (!info) return '';
    
    const mapSettings = info.map_settings || {};
    const coords = info.coordinates || { lat: 60.409388, lng: 11.564448 };
    
    // Hvis en egendefinert embed-URL er satt, bruk den
    if (mapSettings.custom_embed_url) {
      return mapSettings.custom_embed_url;
    }
    
    const zoom = mapSettings.zoom || 15;
    const lat = coords.lat;
    const lng = coords.lng;
    
    // Bruk Google Maps embed som fungerer (2024+ format)
    const distance = Math.round(50000 / Math.pow(2, zoom - 10));
    const timestamp = Date.now();
    
    return `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d${distance}!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sno!2sno!4v${timestamp}`;
  };
  
  const dayLabels = {
    monday: 'Mandag',
    tuesday: 'Tirsdag',
    wednesday: 'Onsdag',
    thursday: 'Torsdag',
    friday: 'Fredag',
    saturday: 'Lørdag',
    sunday: 'Søndag'
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }
  
  if (!companyInfo) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-gray-400">Kunne ikke laste bedriftsinformasjon</p>
        <Button onClick={fetchCompanyInfo} className="mt-4">Prøv igjen</Button>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6" data-testid="company-info-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-7 h-7 text-yellow-400" />
            Bedriftsinformasjon
          </h1>
          <p className="text-gray-400 mt-1">
            Single source of truth - endringer her vises overalt på nettsiden
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchCompanyInfo}
            variant="outline"
            className="border-gray-600 text-gray-300"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Oppdater
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Lagre endringer
          </Button>
        </div>
      </div>
      
      {hasChanges && (
        <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-300 text-sm">Du har ulagrede endringer</span>
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-800 flex-wrap">
          <TabsTrigger value="general" className="data-[state=active]:bg-yellow-600">
            <Building2 className="w-4 h-4 mr-2" />
            Generelt
          </TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-yellow-600">
            <Phone className="w-4 h-4 mr-2" />
            Kontakt
          </TabsTrigger>
          <TabsTrigger value="hours" className="data-[state=active]:bg-yellow-600">
            <Clock className="w-4 h-4 mr-2" />
            Åpningstider
          </TabsTrigger>
          <TabsTrigger value="special-hours" className="data-[state=active]:bg-orange-600">
            <Calendar className="w-4 h-4 mr-2" />
            Ferie/Helligdager
          </TabsTrigger>
          <TabsTrigger value="address" className="data-[state=active]:bg-yellow-600">
            <MapPin className="w-4 h-4 mr-2" />
            Adresse
          </TabsTrigger>
          <TabsTrigger value="map" className="data-[state=active]:bg-blue-600">
            <Map className="w-4 h-4 mr-2" />
            Kart
          </TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-yellow-600">
            <Globe className="w-4 h-4 mr-2" />
            Sosiale medier
          </TabsTrigger>
          <TabsTrigger value="seo" className="data-[state=active]:bg-green-600">
            <Search className="w-4 h-4 mr-2" />
            SEO
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-red-600">
            <Wrench className="w-4 h-4 mr-2" />
            Vedlikehold
          </TabsTrigger>
        </TabsList>
        
        {/* General Tab */}
        <TabsContent value="general">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Grunnleggende informasjon</CardTitle>
              <CardDescription className="text-gray-400">
                Bedriftsnavn, organisasjonsnummer og slagord
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Bedriftsnavn</Label>
                  <Input
                    value={companyInfo.company_name || ''}
                    onChange={(e) => handleFieldChange(null, 'company_name', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Organisasjonsnummer</Label>
                  <Input
                    value={companyInfo.org_number || ''}
                    onChange={(e) => handleFieldChange(null, 'org_number', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Slagord / Tagline</Label>
                <Input
                  value={companyInfo.tagline || ''}
                  onChange={(e) => handleFieldChange(null, 'tagline', e.target.value)}
                  placeholder="F.eks. EU-kontroll, service og reparasjon"
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                />
              </div>
              
              {/* Company Logo */}
              <div>
                <Label className="text-gray-300 mb-2 block">Bedriftslogo (brukes i Schema markup / SEO)</Label>
                <div className="flex items-center gap-4">
                  {companyInfo.logo ? (
                    <div className="relative group">
                      <img
                        src={getMediaUrl(companyInfo.logo)}
                        alt="Bedriftslogo"
                        className="h-16 w-auto rounded border border-gray-600 bg-white p-1"
                        data-testid="company-logo-preview"
                      />
                      <button
                        onClick={() => handleFieldChange(null, 'logo', '')}
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-24 rounded border border-dashed border-gray-600 flex items-center justify-center text-gray-500">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  )}
                  <Button
                    variant="outline"
                    className="border-gray-600 text-gray-300"
                    onClick={() => setIsLogoMediaOpen(true)}
                    data-testid="select-company-logo-btn"
                  >
                    {companyInfo.logo ? 'Bytt logo' : 'Velg logo'}
                  </Button>
                </div>
              </div>

              {/* Logo Media Picker */}
              {isLogoMediaOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
                  <div className="bg-gray-900 rounded-lg w-full max-w-4xl max-h-[80vh] overflow-auto p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-white text-lg font-semibold">Velg bedriftslogo</h3>
                      <Button variant="ghost" onClick={() => setIsLogoMediaOpen(false)}>
                        <X className="w-5 h-5 text-gray-400" />
                      </Button>
                    </div>
                    <AdminMediaLibrary
                      pickerMode={true}
                      onSelect={(media) => {
                        const preferred = media.versions?.medium || media.versions?.large;
                        const rawUrl = preferred?.url || media.url;
                        const logoUrl = toRelativeMediaPath(rawUrl);
                        handleFieldChange(null, 'logo', logoUrl);
                        setIsLogoMediaOpen(false);
                        toast({ title: "Logo valgt!", description: media.title || media.original_filename });
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-gray-300 mb-2 block">Sertifiseringer</Label>
                <div className="space-y-2">
                  {(companyInfo.certifications || []).map((cert, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={cert}
                        onChange={(e) => updateCertification(index, e.target.value)}
                        className="bg-gray-900 border-gray-600 text-white"
                        placeholder="F.eks. Godkjent EU-kontroll verksted"
                      />
                      <Button
                        variant="ghost"
                        onClick={() => removeCertification(index)}
                        className="text-red-400 hover:text-red-300 px-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    onClick={addCertification}
                    className="border-gray-600 text-gray-300"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Legg til sertifisering
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Kontaktinformasjon</CardTitle>
              <CardDescription className="text-gray-400">
                Telefon og e-postadresser
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Telefonnummer (med landskode)</Label>
                  <Input
                    value={companyInfo.contact?.phone || ''}
                    onChange={(e) => handleFieldChange('contact', 'phone', e.target.value)}
                    placeholder="+47 62 97 40 50"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Telefonnummer (visning)</Label>
                  <Input
                    value={companyInfo.contact?.phone_display || ''}
                    onChange={(e) => handleFieldChange('contact', 'phone_display', e.target.value)}
                    placeholder="62 97 40 50"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Generell e-post</Label>
                  <Input
                    type="email"
                    value={companyInfo.contact?.email || ''}
                    onChange={(e) => handleFieldChange('contact', 'email', e.target.value)}
                    placeholder="post@bedrift.no"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Booking e-post</Label>
                  <Input
                    type="email"
                    value={companyInfo.contact?.booking_email || ''}
                    onChange={(e) => handleFieldChange('contact', 'booking_email', e.target.value)}
                    placeholder="booking@bedrift.no"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Opening Hours Tab */}
        <TabsContent value="hours">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                Åpningstider
              </CardTitle>
              <CardDescription className="text-gray-400">
                Administrer åpningstider for hver dag
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(dayLabels).map(([dayKey, dayLabel]) => {
                const dayHours = companyInfo.opening_hours?.[dayKey] || { open: '', close: '', is_open: false };
                
                return (
                  <div key={dayKey} className="flex items-center gap-4 p-3 bg-gray-900 rounded-lg">
                    <div className="w-28">
                      <span className="text-white font-medium">{dayLabel}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={dayHours.is_open}
                        onCheckedChange={(checked) => handleOpeningHoursChange(dayKey, 'is_open', checked)}
                      />
                      <span className={`text-sm ${dayHours.is_open ? 'text-green-400' : 'text-gray-500'}`}>
                        {dayHours.is_open ? 'Åpent' : 'Stengt'}
                      </span>
                    </div>
                    
                    {dayHours.is_open && (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="time"
                          value={dayHours.open}
                          onChange={(e) => handleOpeningHoursChange(dayKey, 'open', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white w-32"
                        />
                        <span className="text-gray-400">-</span>
                        <Input
                          type="time"
                          value={dayHours.close}
                          onChange={(e) => handleOpeningHoursChange(dayKey, 'close', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white w-32"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              
              <div className="pt-4 border-t border-gray-700">
                <Label className="text-gray-300">Merknad om åpningstider</Label>
                <Textarea
                  value={companyInfo.opening_hours_note || ''}
                  onChange={(e) => handleFieldChange(null, 'opening_hours_note', e.target.value)}
                  placeholder="F.eks. Avvikende åpningstider i helligdager"
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Special Hours Tab */}
        <TabsContent value="special-hours">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-400" />
                Ferie og helligdager
              </CardTitle>
              <CardDescription className="text-gray-400">
                Spesielle åpningstider som vises som varselboks på nettsiden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Toggle for showing notice */}
              <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg border border-gray-700">
                <div>
                  <h4 className="text-white font-medium">Vis varselboks på nettsiden</h4>
                  <p className="text-gray-400 text-sm mt-1">
                    Når aktiv, vises en gul varselboks øverst på kontaktsiden
                  </p>
                </div>
                <Switch
                  checked={companyInfo.special_hours?.is_active || false}
                  onCheckedChange={(checked) => handleSpecialHoursChange('is_active', checked)}
                />
              </div>
              
              {/* Preview of how it looks */}
              {companyInfo.special_hours?.is_active && (
                <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                    <div>
                      <p className="text-yellow-400 font-medium">Forhåndsvisning av varselboks</p>
                      <p className="text-gray-300 text-sm">Slik vil det se ut på nettsiden</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Title and message */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-gray-300">Tittel på varselboks</Label>
                  <Input
                    value={companyInfo.special_hours?.title || ''}
                    onChange={(e) => handleSpecialHoursChange('title', e.target.value)}
                    placeholder="F.eks. Påskestengt, Sommerferie, Jul og nyttår"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Beskjed til kunder</Label>
                  <Textarea
                    value={companyInfo.special_hours?.message || ''}
                    onChange={(e) => handleSpecialHoursChange('message', e.target.value)}
                    placeholder="F.eks. Vi holder stengt i påsken fra torsdag 17. april til mandag 21. april."
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                    rows={2}
                  />
                </div>
              </div>
              
              {/* Date range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Fra dato</Label>
                  <Input
                    type="date"
                    value={companyInfo.special_hours?.start_date || ''}
                    onChange={(e) => handleSpecialHoursChange('start_date', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Til dato</Label>
                  <Input
                    type="date"
                    value={companyInfo.special_hours?.end_date || ''}
                    onChange={(e) => handleSpecialHoursChange('end_date', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              
              {/* Special days */}
              <div className="pt-4 border-t border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-gray-300 text-base">Spesifikke dager</Label>
                    <p className="text-gray-500 text-sm">Legg til åpningstider for hver dag i perioden</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={addSpecialDay}
                    className="border-gray-600 text-gray-300"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Legg til dag
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {(companyInfo.special_hours?.hours || []).map((day, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-900 rounded-lg">
                      <Input
                        type="date"
                        value={day.date}
                        onChange={(e) => updateSpecialDay(index, 'date', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white w-40"
                      />
                      <Input
                        value={day.label}
                        onChange={(e) => updateSpecialDay(index, 'label', e.target.value)}
                        placeholder="F.eks. Skjærtorsdag"
                        className="bg-gray-800 border-gray-600 text-white flex-1"
                      />
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={day.is_open}
                          onCheckedChange={(checked) => updateSpecialDay(index, 'is_open', checked)}
                        />
                        <span className={`text-sm w-12 ${day.is_open ? 'text-green-400' : 'text-red-400'}`}>
                          {day.is_open ? 'Åpent' : 'Stengt'}
                        </span>
                      </div>
                      
                      {day.is_open && (
                        <>
                          <Input
                            type="time"
                            value={day.open}
                            onChange={(e) => updateSpecialDay(index, 'open', e.target.value)}
                            className="bg-gray-800 border-gray-600 text-white w-28"
                          />
                          <span className="text-gray-400">-</span>
                          <Input
                            type="time"
                            value={day.close}
                            onChange={(e) => updateSpecialDay(index, 'close', e.target.value)}
                            className="bg-gray-800 border-gray-600 text-white w-28"
                          />
                        </>
                      )}
                      
                      <Button
                        variant="ghost"
                        onClick={() => removeSpecialDay(index)}
                        className="text-red-400 hover:text-red-300 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {(!companyInfo.special_hours?.hours || companyInfo.special_hours.hours.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Ingen spesielle dager lagt til</p>
                      <p className="text-sm">Klikk "Legg til dag" for å legge inn åpningstider for helligdager</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Quick templates */}
              <div className="pt-4 border-t border-gray-700">
                <Label className="text-gray-300 mb-2 block">Hurtigmaler</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                    onClick={() => {
                      handleSpecialHoursChange('title', 'Påskestengt');
                      handleSpecialHoursChange('message', 'Vi holder stengt i påsken. God påske!');
                    }}
                  >
                    Påske
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                    onClick={() => {
                      handleSpecialHoursChange('title', 'Sommerferie');
                      handleSpecialHoursChange('message', 'Verkstedet holder sommerstengt. Vi er tilbake med full kapasitet etter ferien!');
                    }}
                  >
                    Sommerferie
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                    onClick={() => {
                      handleSpecialHoursChange('title', 'Jul og nyttår');
                      handleSpecialHoursChange('message', 'Vi holder stengt mellom jul og nyttår. God jul og godt nyttår!');
                    }}
                  >
                    Jul/Nyttår
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                    onClick={() => {
                      handleSpecialHoursChange('title', 'Avvikende åpningstider');
                      handleSpecialHoursChange('message', 'Vi har avvikende åpningstider i denne perioden.');
                    }}
                  >
                    Generell
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Address Tab */}
        <TabsContent value="address">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Adresse</CardTitle>
              <CardDescription className="text-gray-400">
                Besøksadresse og lokasjon
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Gateadresse</Label>
                <Input
                  value={companyInfo.address?.street || ''}
                  onChange={(e) => handleFieldChange('address', 'street', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Postnummer</Label>
                  <Input
                    value={companyInfo.address?.postal_code || ''}
                    onChange={(e) => handleFieldChange('address', 'postal_code', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Poststed</Label>
                  <Input
                    value={companyInfo.address?.city || ''}
                    onChange={(e) => handleFieldChange('address', 'city', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Kommune</Label>
                  <Input
                    value={companyInfo.address?.municipality || ''}
                    onChange={(e) => handleFieldChange('address', 'municipality', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Fylke</Label>
                  <Input
                    value={companyInfo.address?.county || ''}
                    onChange={(e) => handleFieldChange('address', 'county', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Land</Label>
                  <Input
                    value={companyInfo.address?.country || 'Norge'}
                    onChange={(e) => handleFieldChange('address', 'country', e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
                <div>
                  <Label className="text-gray-300">Latitude (breddegrad)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={companyInfo.coordinates?.lat || ''}
                    onChange={(e) => handleFieldChange('coordinates', 'lat', parseFloat(e.target.value))}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Longitude (lengdegrad)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={companyInfo.coordinates?.lng || ''}
                    onChange={(e) => handleFieldChange('coordinates', 'lng', parseFloat(e.target.value))}
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
              
              {/* Geocoding section */}
              <div className="p-4 bg-gray-900 rounded-lg border border-gray-600">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-400" />
                      Automatisk geocoding
                    </h4>
                    <p className="text-gray-400 text-sm mt-1">
                      Konverter adressen til GPS-koordinater automatisk. Kartet på kontaktsiden vil bruke disse koordinatene.
                    </p>
                  </div>
                  <Button
                    onClick={handleGeocode}
                    disabled={geocoding}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {geocoding ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Søker...
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4 mr-2" />
                        Finn koordinater
                      </>
                    )}
                  </Button>
                </div>
                
                {geocodeResult && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    geocodeResult.success 
                      ? 'bg-green-900/30 border border-green-600/50 text-green-400'
                      : 'bg-red-900/30 border border-red-600/50 text-red-400'
                  }`}>
                    <div className="flex items-center gap-2">
                      {geocodeResult.success ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <AlertCircle className="w-4 h-4" />
                      )}
                      <span className="text-sm">{geocodeResult.message}</span>
                    </div>
                  </div>
                )}
                
                <p className="text-gray-500 text-xs mt-3">
                  Bruker OpenStreetMap Nominatim for geocoding. Husk å lagre endringene etter geocoding.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Map Settings Tab */}
        <TabsContent value="map">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Map className="w-5 h-5 text-blue-400" />
                Kartinnstillinger
              </CardTitle>
              <CardDescription className="text-gray-400">
                Konfigurer hvordan kartet vises på kontaktsiden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Zoom Level */}
              <div>
                <Label className="text-gray-300">Zoom-nivå</Label>
                <div className="flex items-center gap-4 mt-2">
                  <Input
                    type="range"
                    min="1"
                    max="21"
                    value={companyInfo.map_settings?.zoom || 15}
                    onChange={(e) => handleFieldChange('map_settings', 'zoom', parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-white font-mono w-8 text-center">
                    {companyInfo.map_settings?.zoom || 15}
                  </span>
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  Lavere = videre utsikt, Høyere = nærmere (1-21)
                </p>
              </div>
              
              {/* Map Type */}
              <div>
                <Label className="text-gray-300">Karttype</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {[
                    { value: 'roadmap', label: 'Veikart', desc: 'Standard' },
                    { value: 'satellite', label: 'Satellitt', desc: 'Flyfoto' },
                    { value: 'hybrid', label: 'Hybrid', desc: 'Satellitt + veinavn' },
                    { value: 'terrain', label: 'Terreng', desc: 'Topografi' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => handleFieldChange('map_settings', 'map_type', type.value)}
                      className={`p-3 rounded-lg border transition-all text-left ${
                        (companyInfo.map_settings?.map_type || 'roadmap') === type.value
                          ? 'bg-blue-600/30 border-blue-500 text-white'
                          : 'bg-gray-900 border-gray-600 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-gray-400">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Address Query Override */}
              <div className="pt-4 border-t border-gray-700">
                <Label className="text-gray-300">Søkeadresse (valgfritt)</Label>
                <Input
                  value={companyInfo.map_settings?.address_query || ''}
                  onChange={(e) => handleFieldChange('map_settings', 'address_query', e.target.value)}
                  placeholder="F.eks. Sand-tec AS, Industrivegen 4, Sagstua"
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Overstyr adressen som brukes til å finne stedet på kartet. La være tom for å bruke bedriftsadressen.
                </p>
              </div>
              
              {/* Custom Embed URL */}
              <div>
                <Label className="text-gray-300">Egendefinert embed-URL (avansert)</Label>
                <Textarea
                  value={companyInfo.map_settings?.custom_embed_url || ''}
                  onChange={(e) => handleFieldChange('map_settings', 'custom_embed_url', e.target.value)}
                  placeholder="https://www.google.com/maps/embed?..."
                  className="bg-gray-900 border-gray-600 text-white mt-1 font-mono text-sm"
                  rows={3}
                />
                <p className="text-gray-500 text-sm mt-1">
                  Lim inn en fullstendig Google Maps embed-URL her for å overstyre alle andre innstillinger.
                  <a 
                    href="https://www.google.com/maps" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline ml-1"
                  >
                    Åpne Google Maps →
                  </a>
                </p>
              </div>
              
              {/* Preview */}
              <div className="pt-4 border-t border-gray-700">
                <Label className="text-gray-300 mb-3 block">Forhåndsvisning</Label>
                <div className="rounded-lg overflow-hidden border border-gray-600">
                  <iframe
                    src={buildMapPreviewUrl(companyInfo)}
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    loading="lazy"
                    title="Kart forhåndsvisning"
                    className="grayscale-0"
                  ></iframe>
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  Kartet oppdateres automatisk når du lagrer endringene.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Social Media Tab */}
        <TabsContent value="social">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Sosiale medier</CardTitle>
              <CardDescription className="text-gray-400">
                Lenker til sosiale medier-profiler
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Facebook</Label>
                <Input
                  value={companyInfo.social_media?.facebook || ''}
                  onChange={(e) => handleFieldChange('social_media', 'facebook', e.target.value)}
                  placeholder="https://www.facebook.com/dinbedrift"
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Instagram</Label>
                <Input
                  value={companyInfo.social_media?.instagram || ''}
                  onChange={(e) => handleFieldChange('social_media', 'instagram', e.target.value)}
                  placeholder="https://www.instagram.com/dinbedrift"
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">LinkedIn</Label>
                <Input
                  value={companyInfo.social_media?.linkedin || ''}
                  onChange={(e) => handleFieldChange('social_media', 'linkedin', e.target.value)}
                  placeholder="https://www.linkedin.com/company/dinbedrift"
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">SEO & Søkemotorverifisering</CardTitle>
              <CardDescription className="text-gray-400">
                Verifiser nettsiden i søkemotorer og konfigurer SEO-innstillinger
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Search Console */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-white font-medium mb-1 flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Google Search Console
                </h3>
                <p className="text-gray-500 text-sm mb-3">
                  Lim inn verifiseringskoden fra <a href="https://search.google.com/search-console/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Search Console</a> → Innstillinger → Eierverifisering → HTML-tag
                </p>
                <Label className="text-gray-300 text-sm">Verifiseringskode (content-verdien)</Label>
                <Input
                  value={companyInfo.seo?.google_site_verification || ''}
                  onChange={(e) => handleFieldChange('seo', 'google_site_verification', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white mt-1 font-mono text-sm"
                  placeholder="f.eks. abc123xyz..."
                  data-testid="seo-google-verification"
                />
              </div>

              {/* Bing Webmaster */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-white font-medium mb-1 flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M5 3v18l4.5-2.5L15 22l4-2.5V6.5L15 4l-5.5 3L5 3z" fill="#00809D"/><path d="M9.5 18.5L15 22l4-2.5V6.5L15 4l-5.5 3v11.5z" fill="#00BCD4" opacity="0.7"/></svg>
                  Bing Webmaster Tools
                </h3>
                <p className="text-gray-500 text-sm mb-3">
                  Lim inn verifiseringskoden fra <a href="https://www.bing.com/webmasters/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Bing Webmaster Tools</a>
                </p>
                <Label className="text-gray-300 text-sm">Verifiseringskode</Label>
                <Input
                  value={companyInfo.seo?.bing_site_verification || ''}
                  onChange={(e) => handleFieldChange('seo', 'bing_site_verification', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white mt-1 font-mono text-sm"
                  placeholder="f.eks. D4C6B08A37..."
                  data-testid="seo-bing-verification"
                />
              </div>

              {/* Meta Description */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-white font-medium mb-2">Standard meta-beskrivelse</h3>
                <p className="text-gray-500 text-sm mb-3">
                  Vises i søkeresultater når ingen sidespesifikk beskrivelse er satt.
                </p>
                <Textarea
                  value={companyInfo.seo?.meta_description || ''}
                  onChange={(e) => handleFieldChange('seo', 'meta_description', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white mt-1"
                  rows={3}
                  maxLength={160}
                  data-testid="seo-meta-description"
                />
                <p className="text-gray-500 text-xs mt-1">
                  {(companyInfo.seo?.meta_description || '').length}/160 tegn
                </p>
              </div>

              {/* Head Scripts */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-white font-medium mb-2">Egendefinerte head-scripts</h3>
                <p className="text-gray-500 text-sm mb-3">
                  Legg til sporingskoder som Google Analytics, Tag Manager, osv. Disse injiseres i &lt;head&gt;.
                </p>
                <Textarea
                  value={companyInfo.seo?.head_scripts || ''}
                  onChange={(e) => handleFieldChange('seo', 'head_scripts', e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white mt-1 font-mono text-sm"
                  rows={5}
                  placeholder={'<!-- Google Tag Manager -->\n<script>...</script>'}
                  data-testid="seo-head-scripts"
                />
              </div>

              {/* Info box */}
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-300 text-sm font-medium">Automatisk genererte filer</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Disse filene genereres automatisk fra bedriftsinformasjonen:
                  </p>
                  <ul className="text-gray-400 text-sm mt-2 space-y-1">
                    <li>
                      <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">/api/sitemap.xml</a> — Sitemap for søkemotorer
                    </li>
                    <li>
                      <a href="/api/llms.txt" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">/api/llms.txt</a> — LLM-kontekst (kompakt)
                    </li>
                    <li>
                      <a href="/api/llms-full.txt" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">/api/llms-full.txt</a> — LLM-kontekst (full)
                    </li>
                    <li>
                      <a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">/robots.txt</a> — Robots.txt
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance">
          <MaintenanceTab />
        </TabsContent>
      </Tabs>
      
      {/* Last updated info */}
      {companyInfo.updated_at && (
        <div className="text-gray-500 text-sm text-right">
          Sist oppdatert: {new Date(companyInfo.updated_at).toLocaleString('nb-NO')}
          {companyInfo.updated_by && ` av ${companyInfo.updated_by}`}
        </div>
      )}
    </div>
  );
};

export default AdminCompanyInfo;
